import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import FitnessTestWidget from '../components/FitnessTestWidget';
import './BattleStoryMode.css';

const BattleStoryMode = () => {
    const navigate = useNavigate();
    const [currentStory, setCurrentStory] = useState('intro');
    const [backgroundImage, setBackgroundImage] = useState('');
    const [playerStats, setPlayerStats] = useState({
        strength: 0,
        endurance: 0,
        agility: 0,
        balance: 0
    });
    const [testResults, setTestResults] = useState({});
    const [unlockedCharacters, setUnlockedCharacters] = useState([]);
    const [showFitnessTest, setShowFitnessTest] = useState(false);
    const [currentTest, setCurrentTest] = useState(null);

    // All available images for backgrounds
    const allImages = [
        'albert-einstein.jpg', 'alexander-the-great.jpg', 'amelia-earhart.jpg', 'spartacus.jpg',
        'king-leonidas.jpg', 'napoleon-bonaparte.jpg', 'julius-caesar.jpg', 'miyamoto-musashi.jpg',
        'hannibal-barca.jpg', 'attila-the-hun.jpg', 'blackbeard.jpg', 'boudica.jpg', 'thor.jpg',
        'captainamerica.jpg', 'ironman.jpg', 'spiderman.jpg', 'wolverine.jpg', 'deadpool.jpg',
        'zen-monk.jpg', 'geisha-master.jpg', 'tea-master.jpg'
    ];

    // Enhanced story content database with all 8 tests
    const storyContent = {
        intro: {
            title: "⚔️ The Battle Academy Entrance",
            background: 'king-leonidas.jpg',
            text: `Welcome, warrior! You stand before the legendary Battle Academy, where only the strongest and most dedicated fighters earn their place among the elite. 

The ancient gates tower before you, inscribed with the words: "Strength is earned, not given."

Master Kai, the academy's legendary trainer, steps forward with a knowing smile. "So, you wish to join our ranks? Many have tried, few have succeeded. Your journey begins with the Sacred Trials."

He gestures to different training grounds: "Choose your first trial wisely - each path will reveal different aspects of your warrior spirit."`,
            choices: [
                { text: "Begin with the Push-Up Trial", action: "pushup-test", test: "pushup" },
                { text: "Take the Balance Ordeal", action: "balance-test", test: "balance" },
                { text: "Face the Endurance Gauntlet", action: "endurance-test", test: "endurance" },
                { text: "Test your Bench Press Power", action: "benchpress-test", test: "benchpress" },
                { text: "Learn about all trials", action: "trial-preview" }
            ]
        },

        "trial-preview": {
            title: "🏛️ The Eight Sacred Trials",
            background: 'miyamoto-musashi.jpg',
            text: `Master Kai leads you to the Academy's central courtyard, where eight distinct training areas await.

**BODYWEIGHT MASTERY:**
🔥 **The Push-Up Trial** - Foundation of upper body strength and endurance
⚡ **The Pull-Up Challenge** - Functional power that separates warriors from civilians
🏃 **The Endurance Gauntlet** - Cardiovascular trial of continuous movement
⚖️ **The Balance Ordeal** - Core stability and body control mastery
🦵 **The Squat Challenge** - Lower body endurance and foundation

**STRENGTH TRIALS:**
💪 **The Bench Press Test** - Pure pressing power measurement
🏋️ **The Squat Lift Challenge** - Maximum lower body strength
⚡ **The Deadlift Ordeal** - Total body power assessment
🏹 **The Bent-Over Row Trial** - Back strength and pulling mastery

"Each trial unlocks different legendary mentors. Your performance determines which training paths become available. Elite warriors excel across multiple domains!"`,
            choices: [
                { text: "Start with Push-Up Trial", action: "pushup-test", test: "pushup" },
                { text: "Test my Pull-Up Power", action: "pullup-test", test: "pullup" },
                { text: "Take the Sit-Up Challenge", action: "situp-test", test: "situp" },
                { text: "Test my Balance", action: "balance-test", test: "balance" },
                { text: "Face the Endurance Gauntlet", action: "endurance-test", test: "endurance" },
                { text: "Show me Strength Trials", action: "strength-trials" },
                { text: "Return to entrance", action: "intro" }
            ]
        },

        "strength-trials": {
            title: "💪 The Strength Sanctum",
            background: 'spartacus.jpg',
            text: `You enter the Strength Sanctum, where the echoes of legendary lifts still resonate. Spartacus himself designed these trials during his gladiatorial training.

"Raw strength reveals the warrior's foundation," his spirit whispers. "But true power comes from the harmony of all trials."

The four Strength Platforms await:
- **Bench Press**: The measure of pressing dominance - how much can you press?
- **Squat Lift**: Foundation of all power movements - maximum weight you can squat
- **Deadlift**: The ultimate test of total-body strength - lift from the ground  
- **Bent-Over Row**: The forgotten art of pulling power - back strength mastery

Choose your first display of strength, but remember - the Academy measures not just your peak power, but your balance across all domains of fitness.`,
            choices: [
                { text: "Test Bench Press Power", action: "benchpress-test", test: "benchpress" },
                { text: "Squat Lift Challenge", action: "squatlift-test", test: "squatlift" },
                { text: "Deadlift Trial", action: "deadlift-test", test: "deadlift" },
                { text: "Bent-Over Row Test", action: "bentrow-test", test: "bentrow" },
                { text: "Return to main trials", action: "trial-preview" }
            ]
        },

        // Dynamic result stories will be generated based on test performance
        "pushup-complete": { title: "🔥 Push-Up Trial Results", background: 'spartacus.jpg', text: "", choices: [] },
        "pullup-complete": { title: "⚡ Pull-Up Challenge Results", background: 'miyamoto-musashi.jpg', text: "", choices: [] },
        "situp-complete": { title: "🔥 Sit-Up Challenge Results", background: 'spartacus.jpg', text: "", choices: [] },
        "squat-complete": { title: "🦵 Squat Challenge Results", background: 'king-leonidas.jpg', text: "", choices: [] },
        "balance-complete": { title: "⚖️ Balance Ordeal Results", background: 'zen-monk.jpg', text: "", choices: [] },
        "endurance-complete": { title: "🏃 Endurance Gauntlet Results", background: 'amelia-earhart.jpg', text: "", choices: [] },
        "benchpress-complete": { title: "💪 Bench Press Trial Results", background: 'thor.jpg', text: "", choices: [] },
        "squatlift-complete": { title: "🏋️ Squat Lift Trial Results", background: 'thor.jpg', text: "", choices: [] },
        "deadlift-complete": { title: "⚡ Deadlift Trial Results", background: 'thor.jpg', text: "", choices: [] },
        "bentrow-complete": { title: "🏹 Bent-Over Row Trial Results", background: 'miyamoto-musashi.jpg', text: "", choices: [] }
    };

    // Enhanced character unlock logic for all 8 tests
    const checkCharacterUnlocks = (testType, score, rank, userWeight = null) => {
        const newUnlocks = [];

        // Bodyweight tests
        if (testType === 'pushup') {
            if (rank === 'elite') newUnlocks.push('spartacus', 'captain-america', 'thor');
            else if (rank === 'advanced') newUnlocks.push('boudica', 'hannibal-barca');
            else if (rank === 'intermediate') newUnlocks.push('lewis-clark', 'teddy-roosevelt');
        }

        if (testType === 'pullup') {
            if (rank === 'elite') newUnlocks.push('miyamoto-musashi', 'spiderman');
            else if (rank === 'advanced') newUnlocks.push('blackbeard', 'attila-the-hun');
            else if (rank === 'intermediate') newUnlocks.push('marco-polo', 'christopher-columbus');
        }

        if (testType === 'situp') {
            if (rank === 'elite') newUnlocks.push('spartacus', 'captain-america');
            else if (rank === 'advanced') newUnlocks.push('alexander-the-great', 'king-leonidas');
            else if (rank === 'intermediate') newUnlocks.push('roman-centurion', 'gladiator');
        }

        if (testType === 'squat') {
            if (rank === 'elite') newUnlocks.push('king-leonidas', 'spartacus');
            else if (rank === 'advanced') newUnlocks.push('viking-warrior', 'boudica');
            else if (rank === 'intermediate') newUnlocks.push('knight-templar', 'samurai');
        }

        if (testType === 'balance') {
            if (rank === 'elite') newUnlocks.push('zen-monk', 'miyamoto-musashi');
            else if (rank === 'advanced') newUnlocks.push('geisha-master', 'noh-master');
            else if (rank === 'intermediate') newUnlocks.push('tea-master', 'ikebana-master');
        }

        if (testType === 'endurance') {
            if (rank === 'elite') newUnlocks.push('marathon-runner', 'lewis-clark');
            else if (rank === 'advanced') newUnlocks.push('ernest-shackleton', 'amelia-earhart');
            else if (rank === 'intermediate') newUnlocks.push('marco-polo', 'christopher-columbus');
        }

        // Strength tests
        if (testType === 'benchpress') {
            if (rank === 'elite') newUnlocks.push('thor', 'hercules', 'captain-america');
            else if (rank === 'advanced') newUnlocks.push('spartacus', 'alexander-the-great');
            else if (rank === 'intermediate') newUnlocks.push('roman-centurion', 'gladiator');
        }

        if (testType === 'squatlift') {
            if (rank === 'elite') newUnlocks.push('atlas', 'thor', 'strongman');
            else if (rank === 'advanced') newUnlocks.push('spartacus', 'viking-warrior');
            else if (rank === 'intermediate') newUnlocks.push('knight-templar', 'samurai');
        }

        if (testType === 'deadlift') {
            if (rank === 'elite') newUnlocks.push('atlas', 'hercules', 'thor');
            else if (rank === 'advanced') newUnlocks.push('viking-berserker', 'spartacus');
            else if (rank === 'intermediate') newUnlocks.push('blacksmith', 'lumberjack');
        }

        if (testType === 'bentrow') {
            if (rank === 'elite') newUnlocks.push('archer-master', 'miyamoto-musashi');
            else if (rank === 'advanced') newUnlocks.push('robin-hood', 'legolas');
            else if (rank === 'intermediate') newUnlocks.push('hunter', 'ranger');
        }

        return newUnlocks;
    };

    // Enhanced ranking calculation for all test types
    const calculateRank = (testType, score, age, gender, userWeight = null) => {
        // Enhanced standards for all test types
        const standards = {
            pushup: {
                male: {
                    elite: age <= 25 ? 99 : age <= 35 ? 99 : age <= 45 ? 92 : 85,
                    advanced: age <= 25 ? 68 : age <= 35 ? 68 : age <= 45 ? 63 : 57,
                    intermediate: age <= 25 ? 41 : age <= 35 ? 41 : age <= 45 ? 37 : 33,
                    novice: age <= 25 ? 18 : age <= 35 ? 18 : age <= 45 ? 15 : 12,
                    beginner: 1
                },
                female: {
                    elite: age <= 25 ? 56 : age <= 35 ? 56 : age <= 45 ? 51 : 46,
                    advanced: age <= 25 ? 37 : age <= 35 ? 37 : age <= 45 ? 33 : 29,
                    intermediate: age <= 25 ? 19 : age <= 35 ? 19 : age <= 45 ? 16 : 14,
                    novice: age <= 25 ? 5 : age <= 35 ? 5 : age <= 45 ? 4 : 1,
                    beginner: 1
                }
            },
            pullup: {
                male: {
                    elite: age <= 25 ? 37 : age <= 35 ? 37 : age <= 45 ? 34 : 30,
                    advanced: age <= 25 ? 25 : age <= 35 ? 25 : age <= 45 ? 22 : 19,
                    intermediate: age <= 25 ? 14 : age <= 35 ? 14 : age <= 45 ? 12 : 9,
                    novice: age <= 25 ? 5 : age <= 35 ? 5 : age <= 45 ? 3 : 1,
                    beginner: 1
                },
                female: {
                    elite: age <= 25 ? 26 : age <= 35 ? 26 : age <= 45 ? 23 : 19,
                    advanced: age <= 25 ? 15 : age <= 35 ? 15 : age <= 45 ? 13 : 10,
                    intermediate: age <= 25 ? 6 : age <= 35 ? 6 : age <= 45 ? 5 : 3,
                    novice: age <= 25 ? 1 : age <= 35 ? 1 : age <= 45 ? 1 : 1,
                    beginner: 1
                }
            },
            situp: {
                male: {
                    elite: age <= 25 ? 62 : age <= 35 ? 55 : age <= 45 ? 51 : 47,
                    advanced: age <= 25 ? 47 : age <= 35 ? 41 : age <= 45 ? 39 : 35,
                    intermediate: age <= 25 ? 32 : age <= 35 ? 28 : age <= 45 ? 26 : 22,
                    novice: age <= 25 ? 17 : age <= 35 ? 15 : age <= 45 ? 13 : 10,
                    beginner: 1
                },
                female: {
                    elite: age <= 25 ? 54 : age <= 35 ? 45 : age <= 45 ? 42 : 37,
                    advanced: age <= 25 ? 39 : age <= 35 ? 32 : age <= 45 ? 30 : 25,
                    intermediate: age <= 25 ? 25 : age <= 35 ? 20 : age <= 45 ? 18 : 14,
                    novice: age <= 25 ? 11 : age <= 35 ? 8 : age <= 45 ? 6 : 4,
                    beginner: 1
                }
            },
            squat: {
                male: {
                    elite: age <= 25 ? 49 : age <= 35 ? 45 : age <= 45 ? 41 : 37,
                    advanced: age <= 25 ? 37 : age <= 35 ? 34 : age <= 45 ? 31 : 28,
                    intermediate: age <= 25 ? 25 : age <= 35 ? 23 : age <= 45 ? 21 : 19,
                    novice: age <= 25 ? 13 : age <= 35 ? 12 : age <= 45 ? 11 : 10,
                    beginner: 1
                },
                female: {
                    elite: age <= 25 ? 42 : age <= 35 ? 39 : age <= 45 ? 36 : 33,
                    advanced: age <= 25 ? 32 : age <= 35 ? 30 : age <= 45 ? 27 : 24,
                    intermediate: age <= 25 ? 22 : age <= 35 ? 20 : age <= 45 ? 18 : 16,
                    novice: age <= 25 ? 12 : age <= 35 ? 11 : age <= 45 ? 9 : 8,
                    beginner: 1
                }
            },
            balance: {
                male: {
                    elite: age <= 35 ? 45 : age <= 50 ? 37 : age <= 65 ? 27 : 18,
                    advanced: age <= 35 ? 35 : age <= 50 ? 30 : age <= 65 ? 20 : 12,
                    intermediate: age <= 35 ? 25 : age <= 50 ? 20 : age <= 65 ? 15 : 8,
                    novice: age <= 35 ? 15 : age <= 50 ? 12 : age <= 65 ? 10 : 5,
                    beginner: 5
                },
                female: {
                    elite: age <= 35 ? 45 : age <= 50 ? 37 : age <= 65 ? 27 : 18,
                    advanced: age <= 35 ? 35 : age <= 50 ? 30 : age <= 65 ? 20 : 12,
                    intermediate: age <= 35 ? 25 : age <= 50 ? 20 : age <= 65 ? 15 : 8,
                    novice: age <= 35 ? 15 : age <= 50 ? 12 : age <= 65 ? 10 : 5,
                    beginner: 5
                }
            },
            endurance: {
                male: {
                    elite: 9,
                    advanced: 7,
                    intermediate: 5,
                    novice: 3,
                    beginner: 1
                },
                female: {
                    elite: 9,
                    advanced: 7,
                    intermediate: 5,
                    novice: 3,
                    beginner: 1
                }
            },
            // Weight-based tests (using bodyweight multipliers)
            benchpress: {
                male: {
                    elite: 1.95,
                    advanced: 1.5,
                    intermediate: 1.2,
                    novice: 0.9,
                    beginner: 0.5
                },
                female: {
                    elite: 1.35,
                    advanced: 1.0,
                    intermediate: 0.75,
                    novice: 0.5,
                    beginner: 0.3
                }
            },
            squatlift: {
                male: {
                    elite: 2.83,
                    advanced: 2.2,
                    intermediate: 1.75,
                    novice: 1.3,
                    beginner: 0.8
                },
                female: {
                    elite: 2.26,
                    advanced: 1.7,
                    intermediate: 1.3,
                    novice: 0.9,
                    beginner: 0.5
                }
            },
            deadlift: {
                male: {
                    elite: 3.25,
                    advanced: 2.7,
                    intermediate: 2.2,
                    novice: 1.7,
                    beginner: 1.0
                },
                female: {
                    elite: 2.66,
                    advanced: 2.1,
                    intermediate: 1.6,
                    novice: 1.2,
                    beginner: 0.7
                }
            },
            bentrow: {
                male: {
                    elite: 1.5,
                    advanced: 1.2,
                    intermediate: 0.9,
                    novice: 0.7,
                    beginner: 0.4
                },
                female: {
                    elite: 1.0,
                    advanced: 0.8,
                    intermediate: 0.6,
                    novice: 0.4,
                    beginner: 0.2
                }
            }
        };

        const genderStandards = standards[testType]?.[gender.toLowerCase()] || standards[testType]?.['male'];

        if (!genderStandards) return 'beginner';

        // For weight tests, convert to bodyweight ratio
        let compareScore = score;
        if (['benchpress', 'squatlift', 'deadlift', 'bentrow'].includes(testType) && userWeight) {
            compareScore = score / userWeight;
        }

        if (compareScore >= genderStandards.elite) return 'elite';
        if (compareScore >= genderStandards.advanced) return 'advanced';
        if (compareScore >= genderStandards.intermediate) return 'intermediate';
        if (compareScore >= genderStandards.novice) return 'novice';
        return 'beginner';
    };

    // Handle fitness test completion
    const handleTestComplete = (testType, score, userAge, userGender, userWeight = null) => {
        const rank = calculateRank(testType, score, userAge, userGender, userWeight);
        const newUnlocks = checkCharacterUnlocks(testType, score, rank, userWeight);

        setTestResults(prev => ({
            ...prev,
            [testType]: { score, rank, age: userAge, gender: userGender, weight: userWeight }
        }));

        setUnlockedCharacters(prev => [...new Set([...prev, ...newUnlocks])]);

        // Save results to localStorage for milestone integration
        const testData = {
            type: testType,
            score: score,
            rank: rank,
            date: new Date().toISOString(),
            age: userAge,
            gender: userGender,
            weight: userWeight
        };

        const existingTests = JSON.parse(localStorage.getItem('fitnessTestResults') || '[]');
        existingTests.push(testData);
        localStorage.setItem('fitnessTestResults', JSON.stringify(existingTests));

        // Save unlocked characters
        const existingCharacters = JSON.parse(localStorage.getItem('unlockedCharacters') || '[]');
        const updatedCharacters = [...new Set([...existingCharacters, ...newUnlocks])];
        localStorage.setItem('unlockedCharacters', JSON.stringify(updatedCharacters));

        // Generate dynamic story content
        generateTestResultStory(testType, score, rank, userWeight, userAge, userGender);

        setShowFitnessTest(false);
        setCurrentStory(`${testType}-complete`);
    };

    // Generate dynamic story content based on test results
    const generateTestResultStory = (testType, score, rank, userWeight = null, userAge, userGender) => {
        const storyId = `${testType}-complete`;
        let storyText = "";
        let choices = [];

        // Create dynamic story based on test type and performance
        const isWeightBased = ['benchpress', 'squatlift', 'deadlift', 'bentrow'].includes(testType);
        const scoreDisplay = isWeightBased ? `${score} lbs` : `${score} reps`;
        const ratioDisplay = isWeightBased && userWeight ? ` (${(score/userWeight).toFixed(2)}x bodyweight)` : '';

        if (rank === 'elite') {
            storyText = `🏆 LEGENDARY PERFORMANCE! ${scoreDisplay}${ratioDisplay}! 

Master Kai's eyes widen in amazement. "In all my years training warriors, I have rarely witnessed such exceptional ${testType} performance. You have achieved ELITE status!"

The legendary ${getTestMentor(testType)} materializes before you, nodding with deep respect. "This one possesses the ${getTestQuality(testType)} of ancient champions. I shall personally guide their training."

Your elite performance echoes through the Academy halls. Other students stop their training to witness a true champion among them.`;

            choices = [
                { text: `Train with ${getTestMentor(testType)}`, action: "elite-training" },
                { text: "Take another trial", action: "trial-preview" },
                { text: "View unlocked characters", action: "character-gallery" }
            ];
        } else if (rank === 'advanced') {
            storyText = `💪 EXCEPTIONAL! ${scoreDisplay}${ratioDisplay}! 

Master Kai nods approvingly. "Your ${testType} performance demonstrates ADVANCED capabilities. You show the dedication and skill of a true warrior in training."

A respected ${getTestMentor(testType, 'advanced')} approaches. "I see great potential. With proper guidance, you could achieve legendary status."

Your performance has caught the attention of several Academy instructors. New training opportunities await.`;

            choices = [
                { text: `Learn from ${getTestMentor(testType, 'advanced')}`, action: "advanced-training" },
                { text: "Continue trials", action: "trial-preview" },
                { text: "Check progress", action: "progress-review" }
            ];
        } else if (rank === 'intermediate') {
            storyText = `⚡ SOLID EFFORT! ${scoreDisplay}${ratioDisplay}! 

Master Kai smiles encouragingly. "Your ${testType} performance shows INTERMEDIATE skill. You have a strong foundation to build upon."

A seasoned ${getTestMentor(testType, 'intermediate')} offers guidance. "Every master was once a student. Your commitment to improvement will take you far."

The Academy recognizes your potential. Regular training will unlock greater abilities.`;

            choices = [
                { text: `Train with ${getTestMentor(testType, 'intermediate')}`, action: "intermediate-training" },
                { text: "Practice more trials", action: "trial-preview" },
                { text: "Review techniques", action: "technique-review" }
            ];
        } else {
            storyText = `🌟 GREAT START! ${scoreDisplay}${ratioDisplay}! 

Master Kai places an encouraging hand on your shoulder. "Every legendary warrior started their journey with courage, just as you have shown today."

A patient ${getTestMentor(testType, 'beginner')} steps forward. "The path to mastery begins with a single step. Your willingness to test yourself is already a victory."

The Academy doors are open to you. With dedication and training, great things await.`;

            choices = [
                { text: `Begin training with ${getTestMentor(testType, 'beginner')}`, action: "beginner-training" },
                { text: "Try another test", action: "trial-preview" },
                { text: "Learn fundamentals", action: "fundamentals" }
            ];
        }

        if (storyContent[storyId]) {
            storyContent[storyId].text = storyText;
            storyContent[storyId].choices = choices;
        }
    };

    // Helper functions for dynamic story generation
    const getTestMentor = (testType, level = 'elite') => {
        const mentors = {
            pushup: { elite: 'Spartacus', advanced: 'Captain America', intermediate: 'Roman Centurion', beginner: 'Training Sergeant' },
            pullup: { elite: 'Miyamoto Musashi', advanced: 'Spider-Man', intermediate: 'Ninja Master', beginner: 'Climbing Instructor' },
            situp: { elite: 'Spartacus', advanced: 'Captain America', intermediate: 'Fitness Coach', beginner: 'Personal Trainer' },
            squat: { elite: 'King Leonidas', advanced: 'Viking Warrior', intermediate: 'Knight Templar', beginner: 'Squire' },
            balance: { elite: 'Zen Master', advanced: 'Geisha Master', intermediate: 'Tea Master', beginner: 'Balance Instructor' },
            endurance: { elite: 'Marathon Legend', advanced: 'Amelia Earhart', intermediate: 'Explorer', beginner: 'Running Coach' },
            benchpress: { elite: 'Thor', advanced: 'Hercules', intermediate: 'Strongman', beginner: 'Powerlifting Coach' },
            squatlift: { elite: 'Atlas', advanced: 'Thor', intermediate: 'Powerlifter', beginner: 'Strength Coach' },
            deadlift: { elite: 'Atlas', advanced: 'Hercules', intermediate: 'Strongman', beginner: 'Lifting Coach' },
            bentrow: { elite: 'Archer Master', advanced: 'Robin Hood', intermediate: 'Hunter', beginner: 'Archery Instructor' }
        };

        return mentors[testType]?.[level] || 'Master Trainer';
    };

    const getTestQuality = (testType) => {
        const qualities = {
            pushup: 'upper body strength and endurance',
            pullup: 'functional pulling power',
            situp: 'core strength and stability',
            squat: 'lower body endurance',
            balance: 'mind-body harmony',
            endurance: 'cardiovascular mastery',
            benchpress: 'pressing power',
            squatlift: 'lower body strength',
            deadlift: 'total body power',
            bentrow: 'pulling strength'
        };

        return qualities[testType] || 'physical prowess';
    };

    // Handle story navigation
    const handleChoice = (choice) => {
        try {
            soundManager.playButtonSound();
        } catch (error) {
            console.warn('Sound error:', error);
        }

        if (choice.test) {
            setCurrentTest(choice.test);
            setShowFitnessTest(true);
        } else {
            setCurrentStory(choice.action);
        }
    };

    // Select random background image on component mount
    useEffect(() => {
        const randomIndex = Math.floor(Math.random() * allImages.length);
        const selectedImage = allImages[randomIndex];
        setBackgroundImage(`/assets/images/${selectedImage}`);
    }, []);

    // Handle back navigation
    const handleBack = () => {
        try {
            soundManager.playButtonSound();
        } catch (error) {
            console.warn('Sound error:', error);
        }
        navigate(-1);
    };

    const currentStoryData = storyContent[currentStory] || storyContent.intro;

    if (showFitnessTest && currentTest) {
        return (
            <div className="battle-story-container">
                <FitnessTestWidget
                    testType={currentTest}
                    onComplete={handleTestComplete}
                    onCancel={() => setShowFitnessTest(false)}
                />
            </div>
        );
    }

    return (
        <div
            className="battle-story-container"
            style={{
                backgroundImage: `url(/assets/images/${currentStoryData.background || backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="story-overlay"></div>

            <header className="story-header">
                <div className="header-top-row">
                    <button className="back-button" onClick={handleBack}>
                        ← Back
                    </button>
                </div>
            </header>

            <div className="story-content">
                <div className="main-story-area">
                    <div className="story-panel">
                        <h1 className="story-title">{currentStoryData.title}</h1>

                        <div className="story-text">
                            {currentStoryData.text.split('\n').map((paragraph, index) => (
                                <p key={index}>{paragraph}</p>
                            ))}
                        </div>

                        <div className="story-choices">
                            {currentStoryData.choices.map((choice, index) => (
                                <button
                                    key={index}
                                    className={`choice-button ${choice.test ? 'test-choice' : 'story-choice'}`}
                                    onClick={() => handleChoice(choice)}
                                >
                                    {choice.text}
                                    {choice.test && <span className="test-indicator">⚡ FITNESS TEST</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="side-panels">
                    {unlockedCharacters.length > 0 && (
                        <div className="unlocked-characters">
                            <h3>🔓 Unlocked Characters</h3>
                            <div className="character-list">
                                {unlockedCharacters.map(character => (
                                    <span key={character} className="character-badge">
                                        {character.replace('-', ' ').toUpperCase()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {Object.keys(testResults).length > 0 && (
                        <div className="test-results-summary">
                            <h3>📈 Your Test Results</h3>
                            {Object.entries(testResults).map(([testType, result]) => (
                                <div key={testType} className="result-item">
                                    <span className="test-name">{testType.toUpperCase()}</span>
                                    <span className="test-score">
                                        {result.score}{['benchpress', 'squatlift', 'deadlift', 'bentrow'].includes(testType) ? 'lbs' :
                                        testType === 'balance' ? 's' : testType === 'endurance' ? '/10' : ''}
                                    </span>
                                    <span className={`test-rank rank-${result.rank}`}>{result.rank.toUpperCase()}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <footer className="story-footer">
                <div className="story-stats">
                    <span>📊 Characters Unlocked: {unlockedCharacters.length}</span>
                    <span>🏆 Tests Completed: {Object.keys(testResults).length}/8</span>
                </div>
            </footer>
        </div>
    );
};

export default BattleStoryMode;